import { useRef } from 'react';
import { BiogasDataModel } from 'types/types';
import { DataTableStyled } from '../StyledGrid';
import { Column } from 'primereact/column';
import { getOptionsFromDictionary } from 'util/utils';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { useDictionaryValues } from 'services/api/queries';
import { BiogasDataEnum } from 'services/EpdClient';
import { HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';

type Props = {
  data: BiogasDataModel;
  className?: string;
};

type TRow = {
  key: number;
  field: BiogasDataEnum;
  fieldName: React.ReactNode;
  value: string | number | null | undefined;
};

const BiogasDataGridPdf: React.FC<Props> = ({ data, className }) => {
  const biogasDataTable = useRef<any>(null);
  const dictionariesQuery = useDictionaryValues();
  const typeOfBiogasMixOptions = getOptionsFromDictionary(dictionariesQuery.data, EPD_DICTIONARY_CODES.TYPE_OF_BIOGAS_MIX);

  const header = (
    <div className="flex flex-wrap align-items-center justify-content-between gap-2">
      <HeaderCaptionSemiBold>Biogas used in {data.usedInProcessName}</HeaderCaptionSemiBold>
    </div>
  );

  const valueBodyTemplate = (rowData: TRow) => {
    switch (rowData.field) {
      case BiogasDataEnum.TypeOfBiogasMix:
        const typeOfBiogasMixLabel = typeOfBiogasMixOptions.find((x) => Number(x.value) === data?.typeOfBiogasMix)?.label;
        return typeOfBiogasMixLabel;
      case BiogasDataEnum.ClimateImpactIntensity:
        const numberValue = rowData.value as number | undefined;
        return numberValue ? (
          <>
            {numberValue} kg CO<sub>2</sub> eq./MJ
          </>
        ) : (
          ''
        );
      default:
        const stringValue = rowData.value as string | undefined;
        return stringValue ?? '';
    }
  };

  const getColumnsData = (data: BiogasDataModel | undefined) => {
    let index = 0;
    if (!data) return [];

    const columns = [
      {
        key: index++,
        field: BiogasDataEnum.TypeOfBiogasMix,
        fieldName: 'Type of biogas mix',
        value: data.typeOfBiogasMix,
      },
      {
        key: index++,
        field: BiogasDataEnum.ClimateImpactIntensity,
        fieldName: (
          <>
            GWP-GHG intensity (kg CO<sub>2</sub> eq./MJ)
          </>
        ),
        value: data.climateImpactIntensity,
      },
    ] as TRow[];
    return columns;
  };

  const columnsData = getColumnsData(data);

  return (
    <DataTableStyled
      className={className}
      header={header}
      value={columnsData}
      dataKey="key"
      showGridlines
      rowGroupMode="rowspan"
      groupRowsBy="fieldName"
      ref={biogasDataTable}
    >
      <Column field="fieldName" headerStyle={{ display: 'none' }} style={{ verticalAlign: 'top' }} />
      <Column field="value" body={valueBodyTemplate} headerStyle={{ display: 'none' }} style={{ width: '75%' }} />
    </DataTableStyled>
  );
};

export default BiogasDataGridPdf;
